/**
 * Phase 2 · Internal Sales Hub — "New Sale" workspace.
 *
 * Fast-path operator surface for in-person / event / WhatsApp /
 * Instagram / phone sales. Talks to the existing
 * `POST /api/admin/sticker-orders/manual` endpoint (Phase 2-extended)
 * so every completed Sale feeds the same Sales Dashboard the rest of
 * the admin already reads.
 *
 * Key differences from `ManualOrderEntry.jsx`:
 *   - Guest Sale by default (no required customer)
 *   - Product picker over the local Shopify mirror
 *   - Custom items
 *   - Per-line price overrides
 *   - Per-line + entire-sale tax overrides
 *   - Sale-level discount (percentage / fixed)
 *   - Split-tender payments (Phase 1A payments[])
 *   - Historical Sale toggle (backdate transaction date, suppress receipt)
 *   - Underpayment inline block
 *   - Next Sale / Next Historical Sale reset
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, User, Package, DollarSign, Trash2, Plus, CheckCircle2, AlertTriangle, Loader2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const VARIANTS = [
  { value: "white",         label: "White" },
  { value: "blue",          label: "Blue" },
  { value: "gold_crumple",  label: "Gold" },
  { value: "red",           label: "Red" },
  { value: "orange",        label: "Orange" },
  { value: "purple",        label: "Purple" },
];

const PAYMENT_METHODS = [
  { value: "cash",    label: "Cash" },
  { value: "zelle",   label: "Zelle" },
  { value: "cashapp", label: "Cash App" },
  { value: "wise",    label: "WISE" },
  { value: "card",    label: "Card" },
  { value: "shopify", label: "Shopify" },
  { value: "other",   label: "Other" },
];

const SOURCES = [
  { value: "in_person", label: "In-Person" },
  { value: "event",     label: "Event" },
  { value: "whatsapp",  label: "WhatsApp" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook",  label: "Facebook" },
  { value: "tiktok",    label: "TikTok" },
  { value: "phone",     label: "Phone" },
  { value: "other",     label: "Other" },
];

const emptyStickerList = (variant = "gold_crumple") => ({
  id: (crypto.randomUUID?.() || String(Math.random())).slice(0, 8),
  variant, rawText: "", preview: null,   // preview = { items, snapshot }
});

const emptyState = ({ keepHistorical = false, keepDate = "", keepSource = "in_person", keepCustomer = null } = {}) => ({
  guest: !keepCustomer,
  customer: keepCustomer || { customer_id: null, first_name: "", last_name: "", email: "", phone: "" },
  stickerLists: [emptyStickerList()],   // Phase 2 close-out — array, always ≥1
  productLines: [],
  customLines: [],
  lineOverrides: {},          // { "<listId>:<code>": { unit_price, reason } }
  perLineTaxable: {},         // { "<listId>:<code>": bool }
  entireSaleNonTaxable: false,
  saleDiscount: { kind: "none", value: 0 },
  payments: [],
  source: keepSource,
  isHistorical: keepHistorical,
  transactionDate: keepDate,
  receiptPreference: "email",
  notesAdmin: "",
});

export default function AdminNewSale() {
  const [state, setState] = useState(() => emptyState());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [completed, setCompleted] = useState(null);   // { public_id, snap }
  const [keepCustomerNext, setKeepCustomerNext] = useState(false);

  // ── Sticker parse + quote preview per list ────────────────────
  // We debounce per-list via a per-id timer map to avoid cross-list
  // races. Each list owns its own `preview` state slice.
  const listTimers = useRef({});
  const listRefs = useRef({});   // freshest rawText per list id
  useEffect(() => {
    state.stickerLists.forEach((list) => { listRefs.current[list.id] = list.rawText; });
  });
  useEffect(() => {
    state.stickerLists.forEach((list) => {
      if (listTimers.current[list.id]) clearTimeout(listTimers.current[list.id]);
      const currentRaw = list.rawText;
      const currentVar = list.variant;
      if (!currentRaw || !currentRaw.trim()) {
        setState((s) => ({ ...s, stickerLists: s.stickerLists.map((l) =>
          l.id === list.id ? { ...l, preview: null } : l) }));
        return;
      }
      listTimers.current[list.id] = setTimeout(async () => {
        try {
          const parsed = await api.post("/orders/parse", {
            album_slug: "panini-fifa-world-cup-2026",
            raw_text: currentRaw,
          });
          const items = (parsed.data?.items || []).map((i) => ({ code: i.code, qty: i.qty }));
          let snapshot = null;
          if (items.length) {
            const quoted = await api.post("/orders/quote", {
              album_slug: "panini-fifa-world-cup-2026",
              items,
              delivery: { code: "local_pickup" },
              jurisdiction: "US-FL",
              variant: currentVar,
            });
            snapshot = quoted.data;
          }
          if (listRefs.current[list.id] !== currentRaw) return;
          setState((s) => ({ ...s, stickerLists: s.stickerLists.map((l) =>
            l.id === list.id ? { ...l, preview: { items, snapshot } } : l) }));
        } catch { /* silent — server owns totals */ }
      }, 350);
    });
    return () => {
      Object.values(listTimers.current).forEach((t) => t && clearTimeout(t));
    };
  }, [
    state.stickerLists.map((l) => `${l.id}|${l.variant}|${l.rawText}`).join("~"),
  ]);

  // ── Server-authoritative totals preview (frontend approximation) ─
  const totals = useMemo(() => computeTotalsPreview(state), [state]);

  const paidSum = useMemo(
    () => Math.round((state.payments || []).reduce((a, p) => a + (Number(p.amount) || 0), 0) * 100) / 100,
    [state.payments],
  );
  const balance = Math.round((totals.total - paidSum) * 100) / 100;
  const isUnderpaid = state.payments.length > 0 && paidSum + 0.005 < totals.total;
  const hasAnyLine = (
    state.stickerLists.some((l) => (l.preview?.items?.length || 0) > 0)
    || state.productLines.length > 0
    || state.customLines.length > 0
  );
  const canComplete = hasAnyLine && !saving && (state.payments.length === 0 || !isUnderpaid);

  // ── Complete ──────────────────────────────────────────────────
  async function completeSale() {
    if (!canComplete) return;
    setSaving(true); setSaveError("");
    try {
      const payload = buildPayload(state);
      const res = await api.post("/admin/sticker-orders/manual", payload);
      setCompleted({ public_id: res.data.public_id, snap: res.data.pricing_snapshot });
      toast.success(`Sale ${res.data.public_id} completed.`, { duration: 4000 });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "underpaid") {
        setSaveError(detail.message);
      } else {
        setSaveError(formatApiError(detail) || err.message || "Save failed.");
      }
    } finally {
      setSaving(false);
    }
  }

  function nextSale() {
    setCompleted(null); setSaveError("");
    setState(emptyState({
      keepHistorical: state.isHistorical,
      keepDate: state.isHistorical ? state.transactionDate : "",
      keepSource: state.source,
      keepCustomer: keepCustomerNext ? state.customer : null,
    }));
  }

  // ── Completed screen ───────────────────────────────────────────
  if (completed) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-5" data-testid="new-sale-completed">
        <div className="flex items-start gap-3 border-2 border-emerald-600 rounded-2xl p-5 bg-emerald-50">
          <CheckCircle2 className="text-emerald-700" size={28}/>
          <div className="flex-1">
            <div className="kicker">Sale Completed</div>
            <h2 className="text-2xl font-bold mt-1">{completed.public_id}</h2>
            <div className="text-sm mt-1">Total: ${completed.snap?.totals?.total?.toFixed(2)}</div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={keepCustomerNext}
                 onChange={(e) => setKeepCustomerNext(e.target.checked)}
                 data-testid="new-sale-keep-customer-next" />
          Keep same customer for next sale
        </label>
        <div className="flex gap-3">
          <button className="btn btn-primary" onClick={nextSale} data-testid="new-sale-next-btn">
            {state.isHistorical ? "Next Historical Sale" : "Next Customer / New Sale"}
          </button>
          <a className="btn btn-ghost" href={`/admin/all-orders?q=${completed.public_id}`}
             data-testid="new-sale-view-btn">View in All Orders</a>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────
  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5" data-testid="new-sale-page">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="kicker kicker-red">Admin · Sales Hub</div>
          <h1 className="text-3xl font-bold mt-1">New Sale</h1>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="flex items-center gap-2 border-2 border-[var(--ink)] rounded-lg px-3 py-2 bg-white">
            <input type="checkbox" checked={state.isHistorical}
                   onChange={(e) => setState((s) => ({ ...s, isHistorical: e.target.checked }))}
                   data-testid="new-sale-historical-toggle" />
            Historical Sale
          </label>
          {state.isHistorical && (
            <input type="date" value={state.transactionDate}
                    onChange={(e) => setState((s) => ({ ...s, transactionDate: e.target.value }))}
                    className="border-2 border-[var(--ink)] rounded-lg px-2 py-2 bg-white"
                    data-testid="new-sale-historical-date" />
          )}
        </div>
      </header>

      <CustomerBlock state={state} setState={setState} />

      <StickerListsSection state={state} setState={setState} />

      <ProductBlock state={state} setState={setState} />

      <CustomBlock state={state} setState={setState} />

      <AdjustmentsBlock state={state} setState={setState} />

      <PaymentsBlock state={state} setState={setState} totals={totals} paidSum={paidSum} balance={balance} isUnderpaid={isUnderpaid} />

      <SourceReceiptBlock state={state} setState={setState} />

      <TotalsFooter totals={totals} paidSum={paidSum} balance={balance} isUnderpaid={isUnderpaid}
                     canComplete={canComplete} saving={saving} onComplete={completeSale}
                     saveError={saveError} hasAnyLine={hasAnyLine} />
    </div>
  );
}

// ─── Building blocks ────────────────────────────────────────────
function CustomerBlock({ state, setState }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        // Phase 1B master search first (falls back to legacy sticker_orders scan).
        const r = await api.get("/admin/customers/search-master", { params: { q, limit: 8 } });
        setResults(r.data?.results || []);
      } catch { /* silent */ }
      finally { setSearching(false); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  async function pick(row) {
    // Row may come from `master` or `legacy_sticker_orders` — pass identity
    // signals through the resolver so a canonical customer_id is attached.
    let customer_id = row.id || null;
    if (!customer_id && (row.email || row.phone)) {
      // legacy path — resolver will run at write time via the endpoint.
      // For UX we just capture the signals now.
    }
    setState((s) => ({ ...s, guest: false, customer: {
      customer_id,
      first_name: row.first_name || "",
      last_name:  row.last_name  || "",
      email:      row.email      || "",
      phone:      row.phone      || "",
    }}));
    setQ(""); setResults([]);
    toast.success("Customer attached.");
  }

  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-4 bg-white" data-testid="new-sale-customer-block">
      <div className="flex items-center gap-2 mb-3">
        <User size={18} className="text-[var(--panini-red)]" />
        <h2 className="font-bold">Customer</h2>
        <label className="ml-auto flex items-center gap-2 text-sm">
          <input type="checkbox" checked={state.guest}
                 onChange={(e) => setState((s) => ({
                   ...s, guest: e.target.checked,
                   customer: e.target.checked ? { customer_id: null, first_name: "", last_name: "", email: "", phone: "" } : s.customer,
                 }))}
                 data-testid="new-sale-guest-toggle"/>
          Guest Sale
        </label>
      </div>

      {!state.guest && (
        <>
          <div className="relative">
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search by name, email, phone, or CU-…"
                   className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 pr-9"
                   data-testid="new-sale-customer-search" />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {searching ? <Loader2 className="animate-spin" size={16}/> : <Search size={16}/>}
            </div>
            {results.length > 0 && (
              <ul className="absolute z-20 mt-1 w-full bg-white border-2 border-[var(--ink)] rounded-lg max-h-64 overflow-y-auto shadow-lg" data-testid="new-sale-customer-results">
                {results.map((r, i) => (
                  <li key={r.id || i}>
                    <button type="button" onClick={() => pick(r)}
                            className="w-full text-left px-3 py-2 hover:bg-red-50 border-b border-gray-100 last:border-0"
                            data-testid={`new-sale-customer-result-${i}`}>
                      <div className="font-semibold text-sm">{r.display_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.email}</div>
                      <div className="text-xs text-[var(--smoke)]">{r.email || "—"} · {r.phone || "—"}{r.public_customer_id ? ` · ${r.public_customer_id}` : ""}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
            <input placeholder="First name" value={state.customer.first_name}
                   onChange={(e) => setState((s) => ({ ...s, customer: { ...s.customer, first_name: e.target.value }}))}
                   className="border-2 border-[var(--ink)] rounded-lg px-2 py-1" data-testid="new-sale-customer-first" />
            <input placeholder="Last name" value={state.customer.last_name}
                   onChange={(e) => setState((s) => ({ ...s, customer: { ...s.customer, last_name: e.target.value }}))}
                   className="border-2 border-[var(--ink)] rounded-lg px-2 py-1" data-testid="new-sale-customer-last" />
            <input placeholder="Email" value={state.customer.email}
                   onChange={(e) => setState((s) => ({ ...s, customer: { ...s.customer, email: e.target.value }}))}
                   className="border-2 border-[var(--ink)] rounded-lg px-2 py-1" data-testid="new-sale-customer-email" />
            <input placeholder="Phone" value={state.customer.phone}
                   onChange={(e) => setState((s) => ({ ...s, customer: { ...s.customer, phone: e.target.value }}))}
                   className="border-2 border-[var(--ink)] rounded-lg px-2 py-1" data-testid="new-sale-customer-phone" />
          </div>
        </>
      )}
    </section>
  );
}

function StickerListsSection({ state, setState }) {
  function addList() {
    setState((s) => ({ ...s, stickerLists: [...s.stickerLists, emptyStickerList("white")] }));
  }
  function removeList(id) {
    setState((s) => ({
      ...s,
      stickerLists: s.stickerLists.length > 1
        ? s.stickerLists.filter((l) => l.id !== id)
        : s.stickerLists,
      lineOverrides: Object.fromEntries(
        Object.entries(s.lineOverrides).filter(([k]) => !k.startsWith(`${id}:`))
      ),
      perLineTaxable: Object.fromEntries(
        Object.entries(s.perLineTaxable).filter(([k]) => !k.startsWith(`${id}:`))
      ),
    }));
  }
  function updateList(id, patch) {
    setState((s) => ({ ...s, stickerLists: s.stickerLists.map((l) =>
      l.id === id ? { ...l, ...patch } : l) }));
  }
  return (
    <section data-testid="new-sale-sticker-lists">
      {state.stickerLists.map((list, idx) => (
        <div key={list.id} className="mb-3">
          <StickerListBlock
            list={list} index={idx}
            canRemove={state.stickerLists.length > 1}
            onUpdate={(patch) => updateList(list.id, patch)}
            onRemove={() => removeList(list.id)}
            state={state} setState={setState}
          />
        </div>
      ))}
      <button type="button" onClick={addList}
              className="btn btn-sm btn-ghost flex items-center gap-1 mt-1"
              data-testid="new-sale-add-sticker-list-btn">
        <Plus size={14}/> Add Another Sticker List
      </button>
    </section>
  );
}

function StickerListBlock({ list, index, canRemove, onUpdate, onRemove, state, setState }) {
  const items = list.preview?.items || [];
  const snap = list.preview?.snapshot;
  const lines = snap?.lines || [];
  const keyFor = (code) => `${list.id}:${code}`;
  function setOverride(code, unit_price) {
    const k = keyFor(code);
    setState((s) => ({ ...s, lineOverrides: { ...s.lineOverrides,
      [k]: unit_price === "" ? undefined : { unit_price: Number(unit_price) } }}));
  }
  function setTaxable(code, taxable) {
    const k = keyFor(code);
    setState((s) => ({ ...s, perLineTaxable: { ...s.perLineTaxable, [k]: taxable }}));
  }
  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl p-4 bg-white" data-testid={`new-sale-sticker-list-${index}`}>
      <div className="flex items-center gap-3 mb-2">
        <Package size={18} className="text-[var(--panini-red)]"/>
        <h2 className="font-bold">Sticker List {index + 1}</h2>
        <select value={list.variant}
                onChange={(e) => onUpdate({ variant: e.target.value })}
                className="border-2 border-[var(--ink)] rounded-lg px-2 py-1 text-sm"
                data-testid={`new-sale-variant-${index}`}>
          {VARIANTS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>
        {canRemove && (
          <button type="button" onClick={onRemove}
                  className="ml-auto text-red-500 flex items-center gap-1"
                  data-testid={`new-sale-remove-sticker-list-${index}`}
                  aria-label={`Remove sticker list ${index + 1}`}>
            <Trash2 size={14}/><span className="text-xs">Remove</span>
          </button>
        )}
      </div>
      <textarea rows={3} value={list.rawText}
                 onChange={(e) => onUpdate({ rawText: e.target.value })}
                 placeholder="Paste sticker list (BRA-4, ARG-2, POR-13, …)"
                 className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
                 data-testid={`new-sale-sticker-paste-${index}`} />
      {lines.length > 0 && (
        <table className="w-full text-sm mt-3" data-testid={`new-sale-sticker-lines-${index}`}>
          <thead>
            <tr className="text-xs uppercase tracking-widest text-[var(--smoke)] text-left">
              <th>Sticker</th><th>Qty</th><th>Suggested</th><th>Sale Price</th><th>Taxable</th><th className="text-right">Line</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((ln) => {
              const ov = state.lineOverrides[keyFor(ln.code)];
              const eff = ov ? ov.unit_price : ln.unit_price;
              const tax = state.perLineTaxable[keyFor(ln.code)] !== false;
              return (
                <tr key={ln.code} className="border-t border-gray-100">
                  <td className="py-1 font-mono">{ln.code}</td>
                  <td className="py-1">{ln.qty}</td>
                  <td className="py-1">${ln.unit_price?.toFixed(2)}</td>
                  <td className="py-1">
                    <input type="number" step="0.01" min="0"
                           value={ov?.unit_price ?? ln.unit_price}
                           onChange={(e) => setOverride(ln.code, e.target.value)}
                           className="w-20 border rounded px-1 py-0.5"
                           data-testid={`new-sale-override-${index}-${ln.code}`}/>
                  </td>
                  <td className="py-1">
                    <input type="checkbox" checked={tax}
                           onChange={(e) => setTaxable(ln.code, e.target.checked)}
                           data-testid={`new-sale-taxable-${index}-${ln.code}`}/>
                  </td>
                  <td className="py-1 text-right">${(eff * ln.qty).toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ProductBlock({ state, setState }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [mirrorEmpty, setMirrorEmpty] = useState(false);
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/shopify-products/search", { params: { q, limit: 8 } });
        if (!alive) return;
        setResults(r.data?.results || []);
        setMirrorEmpty(!r.data?.mirror_populated && !q);
      } catch { /* silent */ }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  function add(p) {
    setState((s) => ({ ...s, productLines: [...s.productLines, {
      id: p.id, shopify_product_id: p.shopify_product_id,
      shopify_variant_id: p.shopify_variant_id,
      title: p.title, sku: p.sku, handle: p.handle,
      qty: 1, unit_price: p.unit_price, taxable: p.taxable,
    }]}));
  }
  function updateLine(i, patch) {
    setState((s) => ({ ...s, productLines: s.productLines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  }
  function removeLine(i) {
    setState((s) => ({ ...s, productLines: s.productLines.filter((_, j) => j !== i) }));
  }
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-4 bg-white" data-testid="new-sale-product-block">
      <div className="flex items-center gap-2 mb-3">
        <DollarSign size={18} className="text-[var(--panini-red)]"/>
        <h2 className="font-bold">Products</h2>
      </div>
      <div className="relative">
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search products (title / SKU / handle)…"
               className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2"
               data-testid="new-sale-product-search"/>
        {results.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full bg-white border-2 border-[var(--ink)] rounded-lg max-h-64 overflow-y-auto shadow-lg" data-testid="new-sale-product-results">
            {results.map((p, i) => (
              <li key={p.id || i}>
                <button type="button" onClick={() => { add(p); setQ(""); setResults([]); }}
                        className="w-full text-left px-3 py-2 hover:bg-red-50 border-b border-gray-100 last:border-0"
                        data-testid={`new-sale-product-result-${i}`}>
                  <div className="text-sm font-semibold">{p.title}</div>
                  <div className="text-xs text-[var(--smoke)]">{p.sku || "—"} · ${p.unit_price?.toFixed(2)}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {mirrorEmpty && (
        <div className="text-xs text-[var(--smoke)] mt-2 flex items-center gap-2" data-testid="new-sale-mirror-empty">
          <AlertTriangle size={14}/> Local Shopify mirror empty in Preview — use "+ Custom Item" below.
        </div>
      )}
      {state.productLines.length > 0 && (
        <table className="w-full text-sm mt-3" data-testid="new-sale-product-lines">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-[var(--smoke)] text-left">
              <th>Product</th><th>Qty</th><th>Unit</th><th>Taxable</th><th className="text-right">Line</th><th></th>
            </tr>
          </thead>
          <tbody>
            {state.productLines.map((p, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="py-1">{p.title}</td>
                <td className="py-1">
                  <input type="number" min="1" value={p.qty}
                         onChange={(e) => updateLine(i, { qty: Number(e.target.value) || 1 })}
                         className="w-16 border rounded px-1 py-0.5" data-testid={`new-sale-product-qty-${i}`}/>
                </td>
                <td className="py-1">
                  <input type="number" min="0" step="0.01" value={p.unit_price}
                         onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) || 0 })}
                         className="w-20 border rounded px-1 py-0.5"/>
                </td>
                <td className="py-1">
                  <input type="checkbox" checked={p.taxable}
                         onChange={(e) => updateLine(i, { taxable: e.target.checked })} />
                </td>
                <td className="py-1 text-right">${(p.qty * p.unit_price).toFixed(2)}</td>
                <td className="py-1">
                  <button type="button" onClick={() => removeLine(i)} className="text-red-500"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function CustomBlock({ state, setState }) {
  function add() {
    setState((s) => ({ ...s, customLines: [...s.customLines, {
      id: crypto.randomUUID?.() || String(Date.now()),
      description: "", qty: 1, unit_price: 0, taxable: true,
    }]}));
  }
  function updateLine(i, patch) {
    setState((s) => ({ ...s, customLines: s.customLines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  }
  function removeLine(i) {
    setState((s) => ({ ...s, customLines: s.customLines.filter((_, j) => j !== i) }));
  }
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-4 bg-white" data-testid="new-sale-custom-block">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold">Custom Items</h2>
        <button type="button" onClick={add}
                className="btn btn-sm btn-ghost flex items-center gap-1"
                data-testid="new-sale-add-custom-btn"><Plus size={14}/> Add Custom Item</button>
      </div>
      {state.customLines.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-[var(--smoke)] text-left">
              <th>Description</th><th>Qty</th><th>Unit</th><th>Taxable</th><th className="text-right">Line</th><th></th>
            </tr>
          </thead>
          <tbody>
            {state.customLines.map((c, i) => (
              <tr key={c.id} className="border-t border-gray-100">
                <td className="py-1">
                  <input value={c.description}
                         onChange={(e) => updateLine(i, { description: e.target.value })}
                         className="w-full border rounded px-1 py-0.5" data-testid={`new-sale-custom-desc-${i}`}
                         placeholder="Item description" />
                </td>
                <td className="py-1">
                  <input type="number" min="1" value={c.qty}
                         onChange={(e) => updateLine(i, { qty: Number(e.target.value) || 1 })}
                         className="w-16 border rounded px-1 py-0.5" data-testid={`new-sale-custom-qty-${i}`}/>
                </td>
                <td className="py-1">
                  <input type="number" min="0" step="0.01" value={c.unit_price}
                         onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) || 0 })}
                         className="w-20 border rounded px-1 py-0.5" data-testid={`new-sale-custom-price-${i}`}/>
                </td>
                <td className="py-1">
                  <input type="checkbox" checked={c.taxable}
                         onChange={(e) => updateLine(i, { taxable: e.target.checked })} />
                </td>
                <td className="py-1 text-right">${(c.qty * c.unit_price).toFixed(2)}</td>
                <td className="py-1">
                  <button type="button" onClick={() => removeLine(i)} className="text-red-500"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function AdjustmentsBlock({ state, setState }) {
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-4 bg-white" data-testid="new-sale-adjustments-block">
      <h2 className="font-bold mb-3">Discount &amp; Tax</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-[var(--smoke)]">Sale Discount</label>
          <div className="flex gap-2 mt-1">
            <select value={state.saleDiscount.kind}
                    onChange={(e) => setState((s) => ({ ...s, saleDiscount: { ...s.saleDiscount, kind: e.target.value }}))}
                    className="border-2 border-[var(--ink)] rounded-lg px-2 py-1 text-sm"
                    data-testid="new-sale-discount-kind">
              <option value="none">None</option>
              <option value="percentage">Percentage %</option>
              <option value="fixed">Fixed $</option>
            </select>
            {state.saleDiscount.kind !== "none" && (
              <input type="number" min="0" step="0.01" value={state.saleDiscount.value}
                     onChange={(e) => setState((s) => ({ ...s, saleDiscount: { ...s.saleDiscount, value: Number(e.target.value) || 0 }}))}
                     className="border-2 border-[var(--ink)] rounded-lg px-2 py-1 text-sm w-24"
                     data-testid="new-sale-discount-value"/>
            )}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[var(--smoke)]">Tax</label>
          <div className="mt-1">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={state.entireSaleNonTaxable}
                     onChange={(e) => setState((s) => ({ ...s, entireSaleNonTaxable: e.target.checked }))}
                     data-testid="new-sale-non-taxable-toggle" />
              Make Entire Sale Non-Taxable
            </label>
          </div>
        </div>
      </div>
    </section>
  );
}

function PaymentsBlock({ state, setState, totals, paidSum, balance, isUnderpaid }) {
  function addPayment() {
    setState((s) => ({ ...s, payments: [...s.payments, { method: "cash", amount: 0 }] }));
  }
  function updatePayment(i, patch) {
    setState((s) => ({ ...s, payments: s.payments.map((p, j) => j === i ? { ...p, ...patch } : p) }));
  }
  function removePayment(i) {
    setState((s) => ({ ...s, payments: s.payments.filter((_, j) => j !== i) }));
  }
  function payInFull(method = "cash") {
    setState((s) => ({ ...s, payments: [{ method, amount: totals.total }] }));
  }
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-4 bg-white" data-testid="new-sale-payments-block">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold">Payment</h2>
        <div className="flex gap-2">
          <button type="button" onClick={() => payInFull("cash")}
                  className="btn btn-sm btn-ghost" data-testid="new-sale-pay-full-cash">Pay in Full · Cash</button>
          <button type="button" onClick={addPayment}
                  className="btn btn-sm btn-ghost flex items-center gap-1"
                  data-testid="new-sale-add-payment-btn"><Plus size={14}/> Add Payment</button>
        </div>
      </div>
      {state.payments.map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-2" data-testid={`new-sale-payment-${i}`}>
          <select value={p.method} onChange={(e) => updatePayment(i, { method: e.target.value })}
                  className="border-2 border-[var(--ink)] rounded-lg px-2 py-1 text-sm">
            {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <input type="number" min="0" step="0.01" value={p.amount}
                 onChange={(e) => updatePayment(i, { amount: Number(e.target.value) || 0 })}
                 className="w-28 border-2 border-[var(--ink)] rounded-lg px-2 py-1 text-sm"
                 data-testid={`new-sale-payment-amount-${i}`}/>
          <input placeholder="Ref (optional)" value={p.reference || ""}
                 onChange={(e) => updatePayment(i, { reference: e.target.value })}
                 className="flex-1 border-2 border-[var(--ink)] rounded-lg px-2 py-1 text-sm"/>
          <button type="button" onClick={() => removePayment(i)} className="text-red-500"><Trash2 size={14}/></button>
        </div>
      ))}
      <div className="text-sm mt-2 flex gap-4 flex-wrap" data-testid="new-sale-payment-summary">
        <span>Paid: <b>${paidSum.toFixed(2)}</b></span>
        <span>Balance: <b className={balance > 0 ? "text-red-600" : ""}>${balance.toFixed(2)}</b></span>
        {isUnderpaid && (
          <span className="text-red-600 flex items-center gap-1" data-testid="new-sale-underpaid-warning">
            <AlertTriangle size={14}/> Payment is less than the Sale total.
          </span>
        )}
      </div>
    </section>
  );
}

function SourceReceiptBlock({ state, setState }) {
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-4 bg-white" data-testid="new-sale-source-block">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-[var(--smoke)]">Source</label>
          <select value={state.source}
                  onChange={(e) => setState((s) => ({ ...s, source: e.target.value }))}
                  className="w-full border-2 border-[var(--ink)] rounded-lg px-2 py-2 mt-1"
                  data-testid="new-sale-source">
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[var(--smoke)]">Receipt</label>
          <select value={state.receiptPreference}
                  onChange={(e) => setState((s) => ({ ...s, receiptPreference: e.target.value }))}
                  className="w-full border-2 border-[var(--ink)] rounded-lg px-2 py-2 mt-1"
                  data-testid="new-sale-receipt">
            <option value="email">Email (Resend)</option>
            <option value="whatsapp_manual">WhatsApp (manual share)</option>
            <option value="none">No receipt</option>
          </select>
          <div className="text-[11px] text-[var(--smoke)] mt-1">
            WhatsApp is a manual share action — no automated SMS.
          </div>
        </div>
      </div>
    </section>
  );
}

function TotalsFooter({ totals, paidSum, balance, isUnderpaid, canComplete, saving, onComplete, saveError, hasAnyLine }) {
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-4 bg-white sticky bottom-2 shadow-lg" data-testid="new-sale-footer">
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-sm mb-3">
        <div><div className="text-[10px] uppercase text-[var(--smoke)]">Subtotal</div><div className="font-bold">${totals.subtotal.toFixed(2)}</div></div>
        <div><div className="text-[10px] uppercase text-[var(--smoke)]">Discount</div><div className="font-bold">${totals.discount.toFixed(2)}</div></div>
        <div><div className="text-[10px] uppercase text-[var(--smoke)]">Taxable</div><div className="font-bold">${totals.taxableSubtotal.toFixed(2)}</div></div>
        <div><div className="text-[10px] uppercase text-[var(--smoke)]">Tax</div><div className="font-bold">${totals.tax.toFixed(2)}</div></div>
        <div><div className="text-[10px] uppercase text-[var(--smoke)]">Total</div><div className="font-bold text-lg text-[var(--panini-red)]" data-testid="new-sale-grand-total">${totals.total.toFixed(2)}</div></div>
        <div><div className="text-[10px] uppercase text-[var(--smoke)]">Balance</div><div className={`font-bold ${balance > 0 ? "text-red-600" : ""}`}>${balance.toFixed(2)}</div></div>
      </div>
      {saveError && (
        <div className="text-red-600 text-sm mb-2 flex items-center gap-2" data-testid="new-sale-save-error">
          <AlertTriangle size={14}/> {saveError}
        </div>
      )}
      <button type="button" onClick={onComplete} disabled={!canComplete}
              className={`btn btn-primary w-full ${!canComplete ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid="new-sale-complete-btn">
        {saving ? "Completing…" : hasAnyLine ? "Complete Sale" : "Add lines to complete"}
      </button>
    </section>
  );
}

// ─── Helpers ────────────────────────────────────────────────────
// ─── Testable helpers (exported for unit tests) ────────────────
export function _computeTotalsPreview(state) {
  return computeTotalsPreview(state);
}
export function _buildPayload(state) {
  return buildPayload(state);
}
export function _emptyStickerList(variant) {
  return emptyStickerList(variant);
}
export function _emptyState(opts) {
  return emptyState(opts);
}

function computeTotalsPreview(state) {
  // Server owns the persisted value. This preview is for the operator
  // display only.
  const stickerAggregate = state.stickerLists.reduce((acc, list) => {
    const lines = list.preview?.snapshot?.lines || [];
    const rate = list.preview?.snapshot?.totals?.tax?.rate ?? 0.07;
    let subtotal = 0;
    let taxable = 0;
    lines.forEach((ln) => {
      const key = `${list.id}:${ln.code}`;
      const eff = state.lineOverrides[key]?.unit_price ?? ln.unit_price;
      const isTax = state.perLineTaxable[key] !== false;
      subtotal += eff * ln.qty;
      if (isTax) taxable += eff * ln.qty;
    });
    return {
      subtotal: acc.subtotal + subtotal,
      taxable: acc.taxable + taxable,
      rate: acc.rate || rate,
    };
  }, { subtotal: 0, taxable: 0, rate: 0 });
  const stickerSubtotal = stickerAggregate.subtotal;
  const productSubtotal = state.productLines.reduce((a, p) => a + p.qty * p.unit_price, 0);
  const customSubtotal = state.customLines.reduce((a, c) => a + c.qty * c.unit_price, 0);
  const subtotal = stickerSubtotal + productSubtotal + customSubtotal;
  let discount = 0;
  if (state.saleDiscount.kind === "percentage" && state.saleDiscount.value > 0) {
    discount = subtotal * (state.saleDiscount.value / 100);
  } else if (state.saleDiscount.kind === "fixed" && state.saleDiscount.value > 0) {
    discount = Math.min(state.saleDiscount.value, subtotal);
  }
  const taxRate = stickerAggregate.rate || 0.07;
  let taxableSubtotal = 0;
  if (!state.entireSaleNonTaxable) {
    const productTaxable = state.productLines.reduce((a, p) => a + (p.taxable ? p.qty * p.unit_price : 0), 0);
    const customTaxable = state.customLines.reduce((a, c) => a + (c.taxable ? c.qty * c.unit_price : 0), 0);
    taxableSubtotal = stickerAggregate.taxable + productTaxable + customTaxable;
    if (subtotal > 0 && discount > 0) taxableSubtotal = Math.max(0, taxableSubtotal - discount * (taxableSubtotal / subtotal));
  }
  const tax = taxableSubtotal * taxRate;
  const total = subtotal - discount + tax;
  return {
    subtotal: r2(subtotal), discount: r2(discount),
    taxableSubtotal: r2(taxableSubtotal), tax: r2(tax), total: r2(total),
  };
}
function r2(n) { return Math.round(n * 100) / 100; }

function buildPayload(state) {
  // Convert per-list keyed overrides back into a global by-code list.
  // (Backend applies overrides by `code` — with multi-variant lists,
  // an operator applying different overrides to the same code across
  // lists will collapse to the LAST applied value. Extremely rare in
  // practice; documented in remaining risks.)
  const overrides = Object.entries(state.lineOverrides)
    .filter(([, v]) => v && v.unit_price !== undefined)
    .map(([key, v]) => {
      const code = key.includes(":") ? key.split(":", 2)[1] : key;
      return { code, unit_price: v.unit_price, reason: v.reason || null };
    });
  const perLineTax = Object.fromEntries(
    Object.entries(state.perLineTaxable).map(([key, v]) => {
      const code = key.includes(":") ? key.split(":", 2)[1] : key;
      return [code, v];
    })
  );
  // Emit `sticker_lists[]` — one entry per non-empty list — instead of
  // the legacy top-level `items` + `variant` pair.
  const sticker_lists = state.stickerLists
    .filter((l) => (l.preview?.items?.length || 0) > 0)
    .map((l) => ({ variant: l.variant, items: l.preview.items }));
  return {
    album_slug: "panini-fifa-world-cup-2026",
    customer: state.guest ? { customer_id: null } : {
      customer_id: state.customer.customer_id || null,
      first_name: state.customer.first_name || null,
      last_name:  state.customer.last_name  || null,
      email:      state.customer.email      || null,
      phone:      state.customer.phone      || null,
    },
    delivery: { code: "local_pickup" },
    jurisdiction: "US-FL",
    guest_sale: state.guest,
    is_historical_sale: state.isHistorical,
    sticker_lists,
    product_lines: state.productLines,
    custom_lines: state.customLines,
    line_price_overrides: overrides,
    taxability: {
      entire_sale_non_taxable: state.entireSaleNonTaxable,
      per_line: perLineTax,
    },
    sale_discount: state.saleDiscount.kind === "none" ? null : {
      kind: state.saleDiscount.kind, value: state.saleDiscount.value,
    },
    receipt_preference: state.receiptPreference,
    meta: {
      source: state.source,
      original_order_date: state.isHistorical && state.transactionDate
        ? new Date(state.transactionDate + "T12:00:00Z").toISOString() : null,
      payment_status: state.payments.length > 0 ? "paid" : "pending",
      payment_method: state.payments[0]?.method || "cash",
      notify_customer: !state.isHistorical && state.receiptPreference === "email",
      payments: state.payments.map((p) => ({
        amount: Number(p.amount) || 0,
        method: p.method,
        reference: p.reference || null,
      })),
    },
  };
}
